import { useEffect, useState } from 'react';
import {
  Save, Loader2, CheckCircle, Settings, Phone, Mail, MapPin, MessageCircle,
  Type, Palette, Target, Banknote, CreditCard, Gift, Sparkles,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

interface SystemSettings {
  fundraisingGoal?: string;
  fundraisingStartDate?: string;
  fundraisingEndDate?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  monthlyGoalMeals?: string;
  monthlyGoalStudents?: string;
  whatsappThankyouMessage?: string;
  sitePrimaryColor?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactAddress?: string;
  contactWhatsapp?: string;
  whatsappSender?: string;
  cloudwapiKeyStatus?: string;
  cloudwapiKey?: string;
  orgName?: string;
  orgRegNumber?: string;
  fpsId?: string;
  fpsName?: string;
  paymeLink?: string;
  bankName?: string;
  bankAccount?: string;
  accountName?: string;
  ahjimStampMain?: string;
  ahjimStampSub?: string;
}

const SETTING_GROUPS = [
  {
    title: '機構資料',
    icon: Type,
    description: '機構名稱、註冊編號、聯絡方式',
    fields: [
      { key: 'orgName', label: '機構名稱', type: 'text', icon: Type },
      { key: 'orgRegNumber', label: '註冊編號', type: 'text', icon: Type },
      { key: 'contactPhone', label: '聯絡電話', type: 'text', icon: Phone },
      { key: 'contactEmail', label: '聯絡電郵', type: 'email', icon: Mail },
      { key: 'contactWhatsapp', label: 'WhatsApp 號碼', type: 'text', icon: MessageCircle },
      { key: 'whatsappSender', label: 'WhatsApp 發送號碼', type: 'text', icon: MessageCircle },
      { key: 'cloudwapiKey', label: 'CloudWapi API Key', type: 'text', icon: MessageCircle },
      { key: 'contactAddress', label: '地址', type: 'text', icon: MapPin },
    ],
  },
  {
    title: '支付信息',
    icon: Banknote,
    description: 'FPS、PayMe、銀行轉賬資料（前台捐款頁面顯示）',
    fields: [
      { key: 'fpsId', label: 'FPS ID / 轉數快號碼', type: 'text', icon: CreditCard },
      { key: 'fpsName', label: 'FPS 收款人名稱', type: 'text', icon: CreditCard },
      { key: 'paymeLink', label: 'PayMe 連結', type: 'text', icon: CreditCard },
      { key: 'bankName', label: '銀行名稱', type: 'text', icon: Banknote },
      { key: 'bankAccount', label: '銀行帳號', type: 'text', icon: Banknote },
      { key: 'accountName', label: '戶名', type: 'text', icon: Banknote },
    ],
  },
  {
    title: '籌款目標',
    icon: Target,
    description: '籌款金額、時間、影響力目標',
    fields: [
      { key: 'fundraisingGoal', label: '籌款目標（港幣）', type: 'number', icon: Target },
      { key: 'fundraisingStartDate', label: '開始日期', type: 'date', icon: Target },
      { key: 'fundraisingEndDate', label: '結束日期', type: 'date', icon: Target },
      { key: 'monthlyGoalMeals', label: '每月目標飯餐數', type: 'number', icon: Target },
      { key: 'monthlyGoalStudents', label: '每月目標學員數', type: 'number', icon: Target },
    ],
  },
  {
    title: '頁面文案',
    icon: Type,
    description: 'Hero 標題、副標題、感謝訊息',
    fields: [
      { key: 'heroTitle', label: 'Hero 主標題', type: 'text', icon: Type },
      { key: 'heroSubtitle', label: 'Hero 副標題', type: 'text', icon: Type },
      { key: 'whatsappThankyouMessage', label: 'WhatsApp 感謝訊息', type: 'textarea', icon: MessageCircle },
    ],
  },
  {
    title: '阿占回帖',
    icon: Gift,
    description: '捐款成功後的盲盒回帖郵戳配置',
    fields: [
      { key: 'ahjimStampMain', label: '郵戳主文字', type: 'text', icon: Sparkles },
      { key: 'ahjimStampSub', label: '郵戳副文字', type: 'text', icon: Sparkles },
    ],
  },
  {
    title: '品牌設定',
    icon: Palette,
    description: '品牌顏色等視覺設定',
    fields: [
      { key: 'sitePrimaryColor', label: '品牌主色', type: 'color', icon: Palette },
    ],
  },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState<SystemSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await apiFetch<SystemSettings>('/api/admin/settings');
      if (res.success && res.data) {
        setSettings(res.data);
      }
      setLoading(false);
    })();
  }, []);

  const handleChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const camelToSnake = (str: string) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    const payload: Record<string, string> = {};
    for (const [key, value] of Object.entries(settings)) {
      if (value !== undefined) payload[camelToSnake(key)] = String(value);
    }

    const res = await apiFetch('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    setSaving(false);
    if (res.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-6 h-6 text-brand-600" />
          系統設置
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? '保存中...' : '保存設置'}
        </button>
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          設置已保存並生效（無需重新部署）
        </div>
      )}

      {settings.cloudwapiKeyStatus && (
        <div className={`px-4 py-3 rounded-xl flex items-center gap-2 ${settings.cloudwapiKeyStatus === '已配置' ? 'bg-blue-50 border border-blue-200 text-blue-700' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}>
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">CloudWapi API Key：</span>
          <span>{settings.cloudwapiKeyStatus}</span>
          {settings.cloudwapiKeyStatus === '未配置' && (
            <span className="text-xs ml-2">（請通過 wrangler secret put CLOUDWAPI_KEY 配置）</span>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl text-sm">
        💡 <strong>提示：</strong>所有設置保存後會立即寫入數據庫並自動更新前台顯示，<strong>無需重新部署</strong>。
      </div>

      {SETTING_GROUPS.map((group) => (
        <div key={group.title} className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <group.icon className="w-5 h-5 text-brand-600" />
            <div>
              <h2 className="font-bold text-gray-900">{group.title}</h2>
              <p className="text-xs text-gray-500">{group.description}</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {group.fields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {field.label}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={settings[field.key as keyof SystemSettings] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    rows={4}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                ) : field.type === 'color' ? (
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings[field.key as keyof SystemSettings] || '#ea580c'}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings[field.key as keyof SystemSettings] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className="flex-1 p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm font-mono"
                    />
                  </div>
                ) : (
                  <input
                    type={field.type}
                    value={settings[field.key as keyof SystemSettings] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
